import { createFileRoute } from "@tanstack/react-router";
import {
  Chip,
  Label,
  ListBox,
  Select,
  Spinner,
  Table,
  toast,
} from "@heroui/react";
import { Icon } from "@iconify/react";

import {
  AssignOrderEntity,
  useAssignOrders,
} from "@/features/order/use-assign-orders.ts";
import {
  useAssignOrderToCouriers,
  useOpenShiftCouriers,
} from "@/features/order/use-open-shift-couriers.ts";
import { orderTools } from "@/shared/helper/order.ts";
import { PageLayout } from "@/shared/components/page-layout.ui.tsx";
import { OrderStatus } from "@/features/order/types.ts";
import { dt } from "@/shared/helper/datetime";

const statusColorMap: Record<
  OrderStatus,
  "success" | "danger" | "warning" | "accent" | "default"
> = {
  created: "default",
  cancelled: "danger",
  accepted: "accent",
  going_to_sender: "accent",
  decided: "danger",
  submitted: "warning",
  accounted: "warning",
  checked: "default",
  revision: "danger",
  dispatched: "default",
  transit: "accent",
  arrived: "accent",
  assigned: "accent",
  delivers_recipient: "accent",
  completed: "success",
};

export const Route = createFileRoute("/dashboard/assign")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data, isLoading } = useAssignOrders({});

  const orders = (data?.data ?? []) as AssignOrderEntity[];

  return (
    <PageLayout title="Назначение">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : orders.length === 0 ? (
        <div className="flex justify-center py-8 text-neutral-500">
          Нет заказов для назначения
        </div>
      ) : (
        <Table.Root className="mt-4">
          <Table.Content aria-label="Заказы для назначения">
            <Table.Header>
              <Table.Column>Заказ</Table.Column>
              <Table.Column>Маршрут</Table.Column>
              <Table.Column>Отправитель</Table.Column>
              <Table.Column>Слот</Table.Column>
              <Table.Column>Курьер</Table.Column>
            </Table.Header>
            <Table.Body items={orders}>
              {(order: AssignOrderEntity) => (
                <Table.Row id={String(order.id)}>
                  <Table.Cell>
                    <div className="flex flex-col gap-1">
                      <span className="font-bold text-sm text-neutral-800">
                        {orderTools.formatOrderId(order.id)}
                      </span>
                      <Chip
                        color={
                          statusColorMap[order.status as OrderStatus] ?? "default"
                        }
                        size="sm"
                        variant="soft"
                      >
                        {orderTools.status.toString(order.status)}
                      </Chip>
                      <span className="text-xs text-neutral-400">
                        {dt(order.createdAt).format("DD MMM, HH:mm")}
                      </span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className="flex items-center gap-1.5 text-sm font-medium text-neutral-700">
                      <span>{order.fromCity.name}</span>
                      <Icon
                        className="shrink-0 text-neutral-400"
                        icon="ion:arrow-forward"
                        width={12}
                      />
                      <span>{order.toCity.name}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-neutral-800">
                        {order.fromName}
                      </span>
                      <span className="text-xs text-neutral-500">
                        {order.fromPhone}
                      </span>
                      <span className="text-xs text-neutral-400">
                        {orderTools.address.toString({
                          city: order.fromCity.name,
                          address: order.fromAddress,
                          apartment: order.fromApartment,
                          floor: order.fromFloor,
                          entrance: order.fromEntrance,
                        })}
                      </span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs text-neutral-500">
                        {dt(order.pickupSlotStart).format("DD MMM, HH:mm")}
                      </span>
                      <span className="text-xs text-neutral-400">
                        Забрать до {dt(order.pickUpTime).format("HH:mm")}
                      </span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <CourierSelect
                      isAssigned={order.isAssignCourier}
                      orderId={order.id}
                    />
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Content>
        </Table.Root>
      )}
    </PageLayout>
  );
}

const CourierSelect = ({
  orderId,
  isAssigned,
}: {
  orderId: number;
  isAssigned: boolean;
}) => {
  const { data, isLoading } = useOpenShiftCouriers({});
  const { mutateAsync, isPending } = useAssignOrderToCouriers({
    onSuccess: () => {
      toast.success("Курьер назначен");
    },
  });

  const users = data.data.map((item) => ({
    key: `${item.id}`,
    title: `${item.name}, ${item.phoneNumber}`,
  }));

  return (
    <div className="flex items-center gap-2">
      <Select
        isDisabled={isPending || isLoading}
        onSelectionChange={(key) => {
          if (key) {
            void mutateAsync({ orderId, userId: String(key) });
          }
        }}
      >
        <Label>Выбрать курьера</Label>
        <Select.Trigger>
          <Select.Value />
          <Select.Indicator />
        </Select.Trigger>
        <Select.Popover>
          <ListBox items={users}>
            {({ key, title }) => (
              <ListBox.Item id={key} textValue={title}>
                {title}
              </ListBox.Item>
            )}
          </ListBox>
        </Select.Popover>
      </Select>

      {isAssigned && (
        <Chip color="success" size="sm" variant="soft">
          Назначен
        </Chip>
      )}
    </div>
  );
};
